import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import Badge from '../components/Badge';
import SponsorBanner from '../components/SponsorBanner';
import SeatMap from './SeatMap';
import EventCatering from './EventCatering';
import { buildTimetableEntries } from '../utils/timetable';
import { EventBasicInfo, EventTextBlock } from '../components/EventInfoSections';

const INFO_TABS = ['info', 'regeln', 'agb', 'faq'];

function fmt(d) {
  if (!d) return '–';
  return new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const TABS = ['info', 'turniere', 'timetable', 'teilnehmer', 'sitzplan', 'catering', 'sponsoren', 'regeln', 'agb', 'faq'];
const TAB_LABELS = { info: 'Info', turniere: 'Turniere', timetable: 'Timetable', teilnehmer: 'Teilnehmer', sitzplan: 'Sitzplan', catering: 'Catering', sponsoren: 'Sponsoren', regeln: 'Regeln', agb: 'AGB', faq: 'FAQ' };
const TIMETABLE_TYPE_BADGE = { lan: 'bg-sand-50 text-sand-700 border-sand-200', tournament: 'bg-ocean-50 text-ocean-700 border-ocean-200' };
const TIMETABLE_TYPE_LABEL = { lan: 'LAN', tournament: 'Turnier' };

const STATUS_CFG = {
  open:        { label: 'Anmeldung offen', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  in_progress: { label: 'Läuft',           cls: 'bg-ocean-50 text-ocean-700 border-ocean-200' },
  finished:    { label: 'Beendet',          cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  cancelled:   { label: 'Abgesagt',         cls: 'bg-red-50 text-red-600 border-red-200' },
};

export default function EventDetail({ eventId, embedded = false }) {
  const { id: paramId } = useParams();
  const id = eventId ?? paramId;
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [booking, setBooking]       = useState(false);
  const [message, setMessage]       = useState('');
  const [error, setError]           = useState('');
  const [activeTab, setActiveTab]   = useState(embedded ? 'turniere' : 'info');
  const [tournaments, setTournaments] = useState([]);
  const [participants, setParticipants] = useState(null);
  const [hasTicket, setHasTicket] = useState(false);
  const [hasPaidTicket, setHasPaidTicket] = useState(false);
  const [caterings, setCaterings] = useState(null);
  const [sponsors, setSponsors] = useState(null);
  const [customEntries, setCustomEntries] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [tMessages, setTMessages]   = useState({});
  // eslint-disable-next-line no-unused-vars
  const [tLoading, setTLoading]     = useState({});
  // eslint-disable-next-line no-unused-vars
  const [teamInputs, setTeamInputs] = useState({});

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    api.get(`/api/events/${id}`)
      .then((r) => setEvent(r.data))
      .catch(() => { if (!embedded) navigate('/archiv'); })
      .finally(() => setLoading(false));
  }, [id, navigate, embedded]);

  const loadTournaments = useCallback(() => {
    api.get(`/api/events/${id}/tournaments`)
      .then((r) => setTournaments(r.data))
      .catch(() => {});
  }, [id]);

  useEffect(() => { loadTournaments(); }, [loadTournaments]);

  useEffect(() => {
    if (!user) { setHasTicket(false); setHasPaidTicket(false); return; }
    api.get('/api/tickets/my')
      .then((r) => {
        const t = r.data.find((t) => t.event_id === parseInt(id) && t.status !== 'cancelled');
        setHasTicket(!!t);
        setHasPaidTicket(r.data.some((t) => t.event_id === parseInt(id) && ['unlocked', 'checked_in'].includes(t.status)));
      })
      .catch(() => { setHasTicket(false); setHasPaidTicket(false); });
  }, [user, id]);

  useEffect(() => {
    api.get(`/api/events/${id}/catering`).then((r) => setCaterings(r.data)).catch(() => setCaterings([]));
  }, [id]);

  const bookTicket = async () => {
    if (!user) { navigate('/login'); return; }
    setBooking(true); setError(''); setMessage('');
    try {
      await api.post('/api/tickets', { event_id: parseInt(id) });
      setMessage('Ticket gebucht! Überweise jetzt den Betrag und trage die Referenz in dein Ticket ein.');
    } catch (err) { setError(err.response?.data?.detail || 'Fehler beim Buchen'); }
    finally { setBooking(false); }
  };

  const registerTournament = async (t) => {
    if (!user) { navigate('/login'); return; }
    setTLoading((p) => ({ ...p, [t.id]: true }));
    setTMessages((p) => ({ ...p, [t.id]: '' }));
    try {
      await api.post(`/api/tournaments/${t.id}/register`, { team_name: teamInputs[t.id] || null });
      setTMessages((p) => ({ ...p, [t.id]: 'Erfolgreich angemeldet!' }));
      loadTournaments();
    } catch (err) {
      setTMessages((p) => ({ ...p, [t.id]: err.response?.data?.detail || 'Fehler' }));
    } finally { setTLoading((p) => ({ ...p, [t.id]: false })); }
  };

  const unregisterTournament = async (t) => {
    setTLoading((p) => ({ ...p, [t.id]: true }));
    try {
      await api.delete(`/api/tournaments/${t.id}/register`);
      setTMessages((p) => ({ ...p, [t.id]: 'Abgemeldet.' }));
      loadTournaments();
    } catch (err) {
      setTMessages((p) => ({ ...p, [t.id]: err.response?.data?.detail || 'Fehler' }));
    } finally { setTLoading((p) => ({ ...p, [t.id]: false })); }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-2 border-ocean-500 border-t-transparent" /></div>;
  if (!event) return null;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap gap-4 justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold text-ocean-900 tracking-tight">{event.name}</h1>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <Badge status={event.status} />
            {event.location && (
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
                target="_blank" rel="noopener noreferrer"
                className="text-sm text-slate-500 hover:text-ocean-600 no-underline">
                📍 {event.location}
              </a>
            )}
          </div>
        </div>
        {event.status === 'ticket_sale_active' && (
          <Button onClick={bookTicket} loading={booking} size="lg">Ticket buchen</Button>
        )}
      </div>

      {message && <div className="alert-success mb-4">{message}</div>}
      {error   && <div className="alert-error   mb-4">{error}</div>}

      {/* Tabs */}
      <div className="flex flex-wrap border-b border-slate-200 mb-6 gap-0">
        {TABS.filter((t) => !embedded || !INFO_TABS.includes(t))
          .filter((t) => t !== 'timetable' || event.timetable_visible || isAdmin)
          .filter((t) => t !== 'catering' || isAdmin || (caterings && caterings.length > 0))
          .map((t) => (
          <button key={t} onClick={() => {
            setActiveTab(t);
            if (t === 'teilnehmer' && participants === null && (isAdmin || hasTicket)) {
              api.get(`/api/events/${id}/participants`).then((r) => setParticipants(r.data)).catch(() => setParticipants([]));
            }
            if (t === 'sponsoren' && sponsors === null) {
              api.get(`/api/events/${id}/sponsors`).then((r) => setSponsors(r.data)).catch(() => setSponsors([]));
            }
            if (t === 'timetable' && customEntries === null) {
              api.get(`/api/events/${id}/timetable-entries`).then((r) => setCustomEntries(r.data)).catch(() => setCustomEntries([]));
            }
          }}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === t
                ? 'border-ocean-600 text-ocean-700'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}>
            {TAB_LABELS[t]}
            {t === 'turniere' && tournaments.length > 0 && (
              <span className="ml-1.5 text-xs bg-ocean-100 text-ocean-600 px-1.5 py-0.5 rounded font-bold">
                {tournaments.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Info */}
      {activeTab === 'info' && <EventBasicInfo event={event} />}

      {/* Turniere */}
      {activeTab === 'turniere' && (
        <div>
          {tournaments.length === 0 ? (
            <div className="card text-center py-10 text-slate-400 text-sm">
              Noch keine Turniere für dieses Event geplant.
            </div>
          ) : (
            <div className="space-y-4">
              {tournaments.map((t) => {
                const cfg = STATUS_CFG[t.status] || STATUS_CFG.open;
                // eslint-disable-next-line no-unused-vars
                const full = t.participant_count >= t.max_participants;
                return (
                  <div key={t.id} className="card hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/tournaments/${t.id}`)}>
                    <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-bold text-ocean-900 text-base">{t.name}</span>
                          {t.game && (
                            <span className="text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 rounded">
                              {t.game}
                            </span>
                          )}
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${cfg.cls}`}>
                            {cfg.label}
                          </span>
                          {t.category && (
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${t.category === 'fun' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-sand-50 text-sand-700 border-sand-200'}`}>
                              {t.category === 'fun' ? 'Fun' : 'Haupt'}
                            </span>
                          )}
                          {t.format && (
                            <span className="text-xs text-slate-400">
                              {t.format === 'time_trial' ? 'Zeitfahren' : t.format === 'double_elimination' ? 'Double Elim' : 'Single Elim'}
                              {t.team_size > 1 ? ` · ${t.team_size}v${t.team_size}` : ''}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-4 text-xs text-slate-500">
                          {t.start_time && <span>Start: {fmt(t.start_time)}</span>}
                          {t.format !== 'time_trial' && (
                            <span className="text-ocean-600 font-semibold">
                              {t.teams_full ?? 0} vollst. Teams{t.max_teams ? ` / ${t.max_teams}` : ''}
                            </span>
                          )}
                          {t.bracket_generated && <span className="text-ocean-500 font-bold">Bracket aktiv</span>}
                        </div>
                      </div>
                      <Link to={`/tournaments/${t.id}`} onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="secondary">Zum Turnier</Button>
                      </Link>
                    </div>

                    {t.description && (
                      <p className="text-xs text-slate-500 border-t border-slate-100 pt-2 whitespace-pre-wrap line-clamp-2">{t.description}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Timetable */}
      {activeTab === 'timetable' && (
        <div className="card">
          {!event.timetable_visible && isAdmin && (
            <p className="text-xs text-sand-700 bg-sand-50 border border-sand-200 rounded px-3 py-2 mb-4">
              Nur für Admins sichtbar – aktiviere die Timetable im Admin-Bereich, damit sie für alle Teilnehmer sichtbar wird.
            </p>
          )}
          {(() => {
            const entries = buildTimetableEntries(event, tournaments, customEntries || []);
            if (entries.length === 0) {
              return <p className="text-center py-10 text-slate-400 text-sm">Noch keine Termine hinterlegt.</p>;
            }
            return (
              <div className="space-y-2">
                {entries.map((e, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-slate-100 last:border-0 py-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${TIMETABLE_TYPE_BADGE[e.type]}`}>
                        {TIMETABLE_TYPE_LABEL[e.type]}
                      </span>
                      <span className="font-medium text-slate-800">{e.label}</span>
                    </div>
                    <span className="text-slate-500 font-medium">{fmt(e.time)}</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Teilnehmer */}
      {activeTab === 'teilnehmer' && (
        <div className="card">
          <p className="section-title mb-4">Angemeldete Teilnehmer</p>
          {!(isAdmin || hasTicket) ? (
            <div className="text-center py-10 text-slate-400">
              <div className="text-3xl mb-3">🔒</div>
              <p className="font-medium text-slate-600 mb-1">Nur für Teilnehmer sichtbar</p>
              <p className="text-sm">Buche ein Ticket für dieses Event, um die Teilnehmerliste zu sehen.</p>
            </div>
          ) : participants === null ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-ocean-400 border-t-transparent" />
            </div>
          ) : participants.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">Noch keine bestätigten Teilnehmer.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-400 uppercase tracking-wider border-b border-slate-100">
                  <th className="text-left py-2 pr-4 font-medium w-8">#</th>
                  <th className="text-left py-2 pr-4 font-medium">Nickname</th>
                  <th className="text-left py-2 pr-4 font-medium">Sitzplatz</th>
                  <th className="text-left py-2 font-medium">Zahlung</th>
                </tr>
              </thead>
              <tbody>
                {participants.map((p, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                    <td className="py-2.5 pr-4 text-slate-400 font-medium">{i + 1}</td>
                    <td className="py-2.5 pr-4 font-semibold text-slate-800">
                      {p.user_id ? (
                        <Link to={`/users/${p.user_id}`} className="hover:text-ocean-600 hover:underline">
                          {p.nickname}
                        </Link>
                      ) : p.nickname}
                    </td>
                    <td className="py-2.5 pr-4 text-slate-500">{p.seat_label || '–'}</td>
                    <td className="py-2.5">
                      {p.paid
                        ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Bezahlt</span>
                        : <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Ausstehend</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Sitzplan */}
      {activeTab === 'sitzplan' && (
        <SeatMap eventId={id} anonymous={!isAdmin && !hasTicket} />
      )}

      {/* Catering */}
      {activeTab === 'catering' && (
        <EventCatering eventId={id} hasPaidTicket={hasPaidTicket} />
      )}

      {/* Sponsoren */}
      {activeTab === 'sponsoren' && (
        <div>
          {sponsors === null ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-ocean-400 border-t-transparent" />
            </div>
          ) : sponsors.length === 0 ? (
            <div className="card text-center py-10 text-slate-400 text-sm">Keine Sponsoren hinterlegt.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {sponsors.map((s) => (
                <div key={s.id} className="card flex flex-col items-center gap-3 py-6">
                  {s.logo_path ? (
                    <div className="flex items-center justify-center bg-white rounded-lg border border-slate-100 px-4 py-3 w-full" style={{ minHeight: 80 }}>
                      <img src={s.logo_path} alt={s.name} style={{ maxHeight: 64, maxWidth: '100%', objectFit: 'contain' }} />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center w-full bg-slate-50 rounded-lg border border-slate-100 px-4 py-5" style={{ minHeight: 80 }}>
                      <span className="font-bold text-ocean-800 text-lg text-center">{s.name}</span>
                    </div>
                  )}
                  <p className="font-semibold text-slate-800 text-sm text-center">{s.name}</p>
                  {s.website && (
                    <a href={s.website} target="_blank" rel="noreferrer"
                      className="text-xs text-ocean-600 hover:text-ocean-800 hover:underline no-underline text-center break-all">
                      {s.website.replace(/^https?:\/\//, '')}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Text-Tabs */}
      {['regeln', 'agb', 'faq'].includes(activeTab) && (
        <EventTextBlock event={event} field={activeTab === 'regeln' ? 'rules' : activeTab} label={TAB_LABELS[activeTab]} showLabel={false} />
      )}

      {/* Sponsor-Banner */}
      <SponsorBanner eventId={id} />
    </div>
  );
}
